import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Copy, Send, RefreshCw, Download, CheckCircle2, AlertTriangle, Loader2, Plus, X, FileText, Mic,
  ExternalLink, Bot,
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { interviewsAPI, saveBlob } from '@/lib/interviewAPI';
import { safeFormat } from '@/lib/utils';
import { AmrPicker, mergeLinks, STATUS_COLORS, STATUS_LABELS, SUBJECT_TYPES } from './Interviews';
import { YarnsPanel } from '../yarns/Yarns';
import TwinePanel from '@/components/twine/TwinePanel';

const STEPS = [
  { key: 'research', label: 'Research' },
  { key: 'invite', label: 'Invite' },
  { key: 'interview', label: 'Interview' },
  { key: 'followups', label: 'Follow-ups' },
  { key: 'package', label: 'Package' },
  { key: 'approved', label: 'Approved' },
  { key: 'delivered', label: 'Delivered' },
];

const stepDone = (iv) => ({
  research: !!iv.research?.brief,
  invite: !!iv.invited_at || ['interviewing', 'interviewed', 'follow_up', 'compiling', 'review', 'approved', 'delivered'].includes(iv.status),
  interview: (iv.transcript || []).some((t) => t.role === 'user'),
  followups: (iv.followups || []).length > 0 && iv.followups.every((f) => f.answer || f.file_id),
  package: !!iv.package,
  approved: !!iv.approval,
  delivered: !!iv.consent,
});

const Card = ({ title, actions, children }) => (
  <div className="rounded-xl border border-[#E3DDD1] bg-white p-4 md:p-5 mb-4">
    {(title || actions) && (
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        {title && <h3 className="font-semibold text-[#1E2A33]">{title}</h3>}
        <div className="flex gap-2 flex-wrap">{actions}</div>
      </div>
    )}
    {children}
  </div>
);

const red = 'bg-[#A62B1F] hover:bg-[#8C2318] text-white';

// ── Generic editor for the package JSON ───────────────────────────────────────
const titleize = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const FieldEditor = ({ value, onChange, depth = 0, name }) => {
  if (Array.isArray(value)) {
    const template = value[0] && typeof value[0] === 'object' ? Object.fromEntries(Object.keys(value[0]).map((k) => [k, ''])) : '';
    return (
      <div className="space-y-2">
        {value.map((item, i) => (
          <div key={i} className="relative rounded-lg border border-[#E3DDD1] p-3 pr-9 bg-[#FBF8F2]">
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="absolute right-2 top-2 text-[#5C6670] hover:text-[#A62B1F]"><X className="w-4 h-4" /></button>
            <FieldEditor value={item} depth={depth + 1} onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))} />
          </div>
        ))}
        <button type="button" onClick={() => onChange([...value, template])} className="text-xs font-semibold text-[#A62B1F] inline-flex items-center gap-1"><Plus className="w-3 h-3" />Add {name ? titleize(name).replace(/s$/, '').toLowerCase() : 'item'}</button>
      </div>
    );
  }
  if (value && typeof value === 'object') {
    return (
      <div className={depth === 0 ? 'space-y-5' : 'grid gap-2'}>
        {Object.entries(value).map(([k, v]) => (
          <div key={k}>
            <Label className={depth === 0 ? 'text-sm font-semibold text-[#1E2A33]' : 'text-xs text-[#5C6670]'}>{titleize(k)}</Label>
            <div className="mt-1"><FieldEditor name={k} value={v} depth={depth + 1} onChange={(nv) => onChange({ ...value, [k]: nv })} /></div>
          </div>
        ))}
      </div>
    );
  }
  const text = value == null ? '' : String(value);
  return text.length > 90 || name === 'long' || name === 'short' || name === 'caption'
    ? <Textarea value={text} rows={Math.min(14, Math.max(3, Math.ceil(text.length / 90)))} onChange={(e) => onChange(e.target.value)} className="bg-white" />
    : <Input value={text} onChange={(e) => onChange(e.target.value)} className="bg-white" />;
};

// ── Research brief view ──────────────────────────────────────────────────────
const BriefView = ({ brief }) => (
  <div className="space-y-5 text-sm text-[#1E2A33]">
    {brief.summary && <p className="text-base">{brief.summary}</p>}
    {brief.facts?.length > 0 && (
      <div><h4 className="font-semibold mb-1.5">Facts so far</h4>
        <ul className="space-y-1">{brief.facts.map((f, i) => (
          <li key={i}>• {f.fact} <span className="text-[#5C6670]">({f.url ? <a href={f.url} target="_blank" rel="noreferrer" className="underline">{f.source}</a> : f.source})</span></li>))}</ul>
      </div>
    )}
    {brief.vibe && (
      <div><h4 className="font-semibold mb-1.5">The vibe</h4>
        <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
          {Object.entries(brief.vibe).filter(([, v]) => v && (!Array.isArray(v) || v.length)).map(([k, v]) => (
            <div key={k}><dt className="text-xs text-[#5C6670]">{titleize(k)}</dt><dd>{Array.isArray(v) ? v.join(', ') : v}</dd></div>))}
        </dl>
      </div>
    )}
    {brief.story && (
      <div><h4 className="font-semibold mb-1.5">The story so far</h4>
        <p>{brief.story.arc}</p>
        {brief.story.notable_moments?.length > 0 && <ul className="mt-1">{brief.story.notable_moments.map((m, i) => <li key={i}>• {m}</li>)}</ul>}
        {brief.story.momentum && <p className="mt-1"><strong>Momentum:</strong> {brief.story.momentum}{brief.story.momentum_reason ? ` (${brief.story.momentum_reason})` : ''}</p>}
      </div>
    )}
    {brief.stats?.length > 0 && (
      <div><h4 className="font-semibold mb-1.5">Stats</h4>
        <ul>{brief.stats.map((s, i) => <li key={i}>• {s.label}: <strong>{s.value}</strong> <span className="text-[#5C6670]">({s.source}{s.as_of ? `, ${s.as_of}` : ''})</span></li>)}</ul></div>
    )}
    {brief.releases?.length > 0 && (
      <div><h4 className="font-semibold mb-1.5">Releases</h4>
        <ul>{brief.releases.map((r, i) => <li key={i}>• {r.title} <span className="text-[#5C6670]">({[r.type, r.date, r.label, r.source].filter(Boolean).join(', ')})</span></li>)}</ul></div>
    )}
    {brief.gaps?.length > 0 && (
      <div><h4 className="font-semibold mb-1.5">Gaps</h4><ul>{brief.gaps.map((g, i) => <li key={i}>• {g}</li>)}</ul></div>
    )}
    {brief.angles?.length > 0 && (
      <div><h4 className="font-semibold mb-1.5">Angles to explore</h4><ol className="list-decimal pl-5">{brief.angles.map((a, i) => <li key={i}>{a}</li>)}</ol></div>
    )}
  </div>
);

export default function InterviewDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [iv, setIv] = useState(null);
  const [tab, setTab] = useState('overview');
  const [busy, setBusy] = useState('');
  const [pkg, setPkg] = useState(null);
  const [pkgDirty, setPkgDirty] = useState(false);
  const [briefText, setBriefText] = useState(null);
  const [fu, setFu] = useState(null);
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await interviewsAPI.get(id);
      setIv(r.data);
      return r.data;
    } catch (e) {
      toast.error('Interview not found');
      navigate('/interviews');
    }
    return null;
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (iv && !pkgDirty) setPkg(iv.package); }, [iv, pkgDirty]);
  useEffect(() => { if (iv && fu === null) setFu(iv.followups || []); }, [iv, fu]);

  // Poll while something runs in the background (research, drafting) or the interview is live
  useEffect(() => {
    if (!iv) return undefined;
    const atDesk = ['queued', 'claimed'].includes(iv.desk?.status);
    const live = iv.research?.status === 'running' || iv.package_status === 'drafting' || iv.status === 'interviewing' || atDesk;
    if (!live) return undefined;
    const t = setInterval(load, atDesk ? 20000 : iv.status === 'interviewing' ? 6000 : 3000);
    return () => clearInterval(t);
  }, [iv, load]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const r = await fn();
      if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok);
      await load();
      return r;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Something went wrong');
      return null;
    } finally { setBusy(''); }
  };

  if (!iv) return <Layout title="Interview"><div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div></Layout>;

  const done = stepDone(iv);
  const s = iv.subject || {};
  const userTurns = (iv.transcript || []).filter((t) => t.role === 'user').length;
  const researching = iv.research?.status === 'running';
  const drafting = iv.package_status === 'drafting';
  const deskStatus = iv.desk?.status;
  const atDesk = ['queued', 'claimed'].includes(deskStatus);
  const sendToDesk = () => {
    if (iv.package && !window.confirm('Send this to the desk for a fresh draft? The current package stays until the new one comes back.')) return;
    const note = window.prompt('Anything the desk should focus on? (optional)', '') ;
    if (note === null) return;
    run('desk', () => interviewsAPI.toDesk(id, note), 'Sent to the desk');
  };
  const deskLine = atDesk && (
    <div className="flex items-center gap-2 text-sm rounded-lg bg-[#F3EFE7] px-3 py-2">
      <Bot className="w-4 h-4 text-[#A62B1F]" />
      {deskStatus === 'claimed'
        ? <span>The desk agent{iv.desk.claimed_by ? ` (${iv.desk.claimed_by})` : ''} is researching and writing. Started {safeFormat(iv.desk.claimed_at, 'MMM d, h:mm a')}.</span>
        : <span>Queued for the desk since {safeFormat(iv.desk.queued_at, 'MMM d, h:mm a')}. It's picked up on the next run.</span>}
      {deskStatus === 'queued' && <button className="ml-auto text-xs font-semibold text-[#A62B1F]" onClick={() => run('cancel', () => interviewsAPI.cancelDesk(id), 'Taken off the desk')}>Take back</button>}
    </div>
  );

  const copyLink = () => { navigator.clipboard.writeText(iv.public_link); toast.success('Link copied'); };
  const invite = (channels) => run('invite', () => interviewsAPI.invite(id, channels), (r) => {
    const res = r.data.results || {};
    const sent = Object.entries(res).filter(([, v]) => v.success).map(([k]) => (k === 'sms' ? 'text' : k));
    const failed = Object.entries(res).filter(([, v]) => !v.success).map(([k, v]) => `${k === 'sms' ? 'Text' : 'Email'}: ${v.error}`);
    if (failed.length) setTimeout(() => toast.error(failed.join(' · ')), 50);
    return sent.length ? `Invite sent by ${sent.join(' and ')}` : 'Nothing was sent. Copy the link and send it yourself.';
  });

  const downloadPdf = async () => {
    const r = await interviewsAPI.pdf(id).catch(() => null);
    if (r) saveBlob(r.data, `${s.name}-one-sheet.pdf`); else toast.error('Could not build the PDF');
  };
  const downloadAudio = async (sid) => {
    const r = await interviewsAPI.audio(id, sid).catch(() => null);
    if (r) saveBlob(r.data, `${s.name}-interview-${sid.slice(0, 6)}.wav`); else toast.error('No audio for that session');
  };
  const openFile = async (fid, name) => {
    const r = await interviewsAPI.file(id, fid).catch(() => null);
    if (r) saveBlob(r.data, name || 'upload'); else toast.error('File not found');
  };
  const exportJson = async () => {
    const r = await interviewsAPI.exportJson(id);
    saveBlob(new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' }), `${s.name}-press-kit.json`);
  };

  return (
    <Layout title="Interview">
      <button onClick={() => navigate('/interviews')} className="text-sm text-[#5C6670] inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-4 h-4" />All interviews</button>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-[#1E2A33]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{s.name}</h1>
          <p className="text-sm text-[#5C6670]">
            {SUBJECT_TYPES.find((t) => t.id === iv.subject_type)?.label} · Requested by {iv.requested_by?.name}
            {!iv.is_requester && ' (only they can give final approval)'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${STATUS_COLORS[iv.status] || ''} border-0`}>{STATUS_LABELS[iv.status] || iv.status}</Badge>
          <Button variant="outline" size="sm" onClick={copyLink}><Copy className="w-4 h-4 mr-1" />Copy their link</Button>
        </div>
      </div>

      {/* Progress */}
      <div className="flex gap-1 mb-5 overflow-x-auto">
        {STEPS.map((st) => (
          <div key={st.key} className="flex-1 min-w-[84px]">
            <div className={`h-1.5 rounded-full ${done[st.key] ? 'bg-[#A62B1F]' : 'bg-[#E3DDD1]'}`} />
            <div className={`text-[11px] mt-1 ${done[st.key] ? 'text-[#1E2A33] font-semibold' : 'text-[#5C6670]'}`}>{st.label}</div>
          </div>
        ))}
      </div>

      {(!iv.ai_ready || !iv.voice_ready || !iv.sms_ready) && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-4 text-xs text-amber-900">
          Setup still needed on the server: {[!iv.ai_ready && 'AI (ANTHROPIC_API_KEY)', !iv.voice_ready && 'voice interviews (DEEPGRAM_API_KEY)', !iv.sms_ready && 'texting (Twilio keys)'].filter(Boolean).join(', ')}.
          {!iv.voice_ready && ' Chat interviews still work.'}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="research">Research{researching && <Loader2 className="w-3 h-3 ml-1 animate-spin" />}</TabsTrigger>
          <TabsTrigger value="interview">Interview{userTurns ? ` (${userTurns})` : ''}</TabsTrigger>
          <TabsTrigger value="followups">Follow-ups</TabsTrigger>
          <TabsTrigger value="package">Package{drafting && <Loader2 className="w-3 h-3 ml-1 animate-spin" />}</TabsTrigger>
        </TabsList>

        {/* ── Overview ─────────────────────────────────────────── */}
        <TabsContent value="overview">
          <Card title="Next step">
            {!iv.research?.brief ? (
              <p className="text-sm">{researching ? 'Research is running. The brief usually takes a minute or two.' : 'Run the research to build the brief the interviewer reads before the call.'}</p>
            ) : !done.interview ? (
              <div className="text-sm space-y-3">
                <p>The brief is ready. Send {s.first_name} their invite, or copy the link and send it yourself.</p>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" className={red} disabled={busy === 'invite'} onClick={() => invite(['sms', 'email'])}><Send className="w-4 h-4 mr-1" />Send invite</Button>
                  <Button size="sm" variant="outline" onClick={copyLink}><Copy className="w-4 h-4 mr-1" />Copy link</Button>
                  <Button size="sm" variant="outline" onClick={() => window.open(iv.public_link, '_blank')}><ExternalLink className="w-4 h-4 mr-1" />Open as them</Button>
                </div>
              </div>
            ) : !iv.package ? (
              <div className="text-sm space-y-3">
                {atDesk ? deskLine : <>
                  <p>{s.first_name} has done the interview ({userTurns} answers). Send it to the desk for deep research and a reviewed draft, or make a quick draft now.</p>
                  {deskStatus === 'error' && <p className="text-[#A62B1F]">The desk couldn't finish last time: {iv.desk.error}</p>}
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setTab('followups')}>Follow-ups</Button>
                    {iv.desk_ready && <Button size="sm" className={red} disabled={busy === 'desk'} onClick={sendToDesk}><Bot className="w-4 h-4 mr-1" />Send to the desk</Button>}
                    <Button size="sm" variant={iv.desk_ready ? 'outline' : 'default'} className={iv.desk_ready ? '' : red} disabled={drafting || busy === 'compile'} onClick={() => run('compile', () => interviewsAPI.compile(id), 'Drafting the package…')}><FileText className="w-4 h-4 mr-1" />Quick draft</Button>
                  </div>
                </>}
              </div>
            ) : !iv.approval ? (
              <p className="text-sm">The draft is ready for review in the Package tab. {iv.is_requester ? 'You give the final approval.' : `${iv.requested_by?.name} gives the final approval.`}</p>
            ) : !iv.consent ? (
              <p className="text-sm">Approved by {iv.approval.by_name}. Waiting for {s.first_name} to accept the terms and open their package.</p>
            ) : (
              <p className="text-sm">Delivered. {s.first_name} accepted the terms on {safeFormat(iv.consent.at, 'MMM d, yyyy')} as “{iv.consent.name}”.</p>
            )}
          </Card>

          <Card title="Profile" actions={profile
            ? [<Button key="c" size="sm" variant="outline" onClick={() => setProfile(null)}>Cancel</Button>,
              <Button key="s" size="sm" className={red} onClick={async () => {
                await run('profile', () => interviewsAPI.update(id, {
                  subject: { ...s, ...profile.subject }, notes: profile.notes, sms_opt_in: profile.sms_opt_in,
                  amr_artist_id: profile.amr_artist_id,
                  links: profile.links.filter((l) => l.url.trim()),
                }), 'Saved');
                setProfile(null);
              }}>Save</Button>]
            : [<Button key="e" size="sm" variant="outline" onClick={() => setProfile({ subject: { ...s }, notes: iv.notes || '', sms_opt_in: !!iv.sms_opt_in, amr_artist_id: iv.amr_artist_id || '', amr_name: '', links: [...(iv.links || []), { url: '' }] })}>Edit</Button>]}>
            {profile ? (
              <div className="grid gap-3 text-sm">
                <div className="grid sm:grid-cols-2 gap-3">
                  {['name', 'first_name', 'phone', 'email', 'hometown'].map((k) => (
                    <div key={k}><Label className="text-xs">{titleize(k)}</Label><Input value={profile.subject[k] || ''} onChange={(e) => setProfile({ ...profile, subject: { ...profile.subject, [k]: e.target.value } })} /></div>
                  ))}
                </div>
                {iv.amr_ready && (
                  <div><Label className="text-xs">AMR artist</Label>
                    <div className="mt-1"><AmrPicker value={profile.amr_artist_id} valueName={profile.amr_name}
                      onPick={async (a) => {
                        setProfile((p) => ({ ...p, amr_artist_id: a?.id || '', amr_name: a?.name || '' }));
                        if (!a) return;
                        try {
                          const { data: d } = await interviewsAPI.amrArtist(a.id);
                          setProfile((p) => ({
                            ...p,
                            subject: { ...p.subject, phone: p.subject.phone || d.phone || '', email: p.subject.email || d.email || '', hometown: p.subject.hometown || d.hometown || '' },
                            links: mergeLinks(p.links.map((l) => l.url), d.links).map((url) => ({ url })),
                          }));
                        } catch (e) { toast.error('Linked, but could not load their AMR details'); }
                      }} /></div>
                  </div>
                )}
                <label className="flex gap-2"><input type="checkbox" checked={profile.sms_opt_in} onChange={(e) => setProfile({ ...profile, sms_opt_in: e.target.checked })} />Agreed to receive texts</label>
                <div><Label className="text-xs">Links</Label>
                  <div className="space-y-2 mt-1">{profile.links.map((l, i) => (
                    <Input key={i} value={l.url} placeholder="https://" onChange={(e) => setProfile({ ...profile, links: profile.links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} />))}
                    <button type="button" className="text-xs font-semibold text-[#A62B1F]" onClick={() => setProfile({ ...profile, links: [...profile.links, { url: '' }] })}>+ Add link</button>
                  </div>
                </div>
                <div><Label className="text-xs">Notes</Label><Textarea rows={3} value={profile.notes} onChange={(e) => setProfile({ ...profile, notes: e.target.value })} /></div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><span className="text-[#5C6670]">Mobile:</span> {s.phone || '—'} {s.phone && !iv.sms_opt_in && <span className="text-xs text-amber-700">(no texting consent)</span>}</div>
                <div><span className="text-[#5C6670]">Email:</span> {s.email || '—'}</div>
                <div><span className="text-[#5C6670]">Hometown:</span> {s.hometown || '—'}</div>
                {iv.amr_ready && <div><span className="text-[#5C6670]">AMR portal:</span> {iv.amr_artist_id ? 'Linked' : 'Matched by name at research time'}</div>}
                <div className="sm:col-span-2"><span className="text-[#5C6670]">Links:</span>
                  <ul className="mt-1">{(iv.links || []).map((l, i) => <li key={i} className="truncate"><a href={l.url.startsWith('http') ? l.url : `https://${l.url}`} target="_blank" rel="noreferrer" className="text-[#A62B1F] underline">{l.url}</a></li>)}</ul>
                </div>
                {iv.notes && <div className="sm:col-span-2"><span className="text-[#5C6670]">Notes:</span> {iv.notes}</div>}
              </div>
            )}
          </Card>

          <YarnsPanel link={{ type: 'interview', id, label: s.name }} />

          <TwinePanel className="mb-4"
            subject={iv.contact_id ? { type: 'contact', id: iv.contact_id, label: s.name } : { type: 'interview', id, label: s.name }}
            mendFrom={(iv.transcript || []).length ? { type: 'interview', id } : null} />

          {(iv.messages_log || []).length > 0 && (
            <Card title="Messages sent">
              <ul className="text-sm space-y-1">
                {[...iv.messages_log].reverse().map((m, i) => (
                  <li key={i} className="flex gap-2 items-center">
                    {m.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                    <span>{m.kind} by {m.channel === 'sms' ? 'text' : 'email'} to {m.to}</span>
                    <span className="text-[#5C6670]">· {safeFormat(m.at, 'MMM d, h:mm a')}</span>
                    {m.error && <span className="text-amber-700">· {m.error}</span>}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </TabsContent>

        {/* ── Research ─────────────────────────────────────────── */}
        <TabsContent value="research">
          <Card title="Research brief" actions={[
            <Button key="r" size="sm" variant="outline" disabled={researching || busy === 'research'} onClick={() => run('research', () => interviewsAPI.research(id), 'Research started')}>
              <RefreshCw className={`w-4 h-4 mr-1 ${researching ? 'animate-spin' : ''}`} />{iv.research?.brief ? 'Re-run research' : 'Run research'}
            </Button>,
            iv.research?.brief && <Button key="e" size="sm" variant="outline" onClick={() => setBriefText(briefText === null ? JSON.stringify(iv.research.brief, null, 2) : null)}>{briefText === null ? 'Edit' : 'Cancel'}</Button>,
          ]}>
            {iv.research?.status === 'error' && <p className="text-sm text-[#A62B1F] mb-3">Research failed: {iv.research.error}</p>}
            {researching && <p className="text-sm text-[#5C6670] mb-3">Reading their profiles and searching the web…</p>}
            {briefText !== null ? (
              <div className="space-y-2">
                <Textarea rows={22} value={briefText} onChange={(e) => setBriefText(e.target.value)} className="font-mono text-xs" />
                <Button size="sm" className={red} onClick={async () => {
                  let parsed;
                  try { parsed = JSON.parse(briefText); } catch (e) { toast.error('That is not valid JSON'); return; }
                  await run('brief', () => interviewsAPI.saveBrief(id, parsed), 'Brief saved');
                  setBriefText(null);
                }}>Save brief</Button>
              </div>
            ) : iv.research?.brief ? <BriefView brief={iv.research.brief} /> : !researching && <p className="text-sm text-[#5C6670]">No brief yet.</p>}
          </Card>
          {(iv.research?.sources || []).length > 0 && (
            <Card title="Sources">
              <ul className="text-sm space-y-1.5">
                {iv.research.sources.map((src, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    {src.error ? <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" /> : <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />}
                    <div className="min-w-0"><strong>{src.source}</strong>{src.url && <> · <a href={src.url} target="_blank" rel="noreferrer" className="underline text-[#5C6670] break-all">{src.url}</a></>}
                      {src.error && <div className="text-xs text-amber-700">{src.error}</div>}</div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </TabsContent>

        {/* ── Interview ────────────────────────────────────────── */}
        <TabsContent value="interview">
          {(iv.sessions || []).length > 0 && (
            <Card title="Voice sessions">
              <ul className="text-sm space-y-1">
                {iv.sessions.map((ses) => (
                  <li key={ses.id} className="flex items-center gap-3 flex-wrap">
                    <Mic className="w-4 h-4 text-[#A62B1F]" />
                    <span>{safeFormat(ses.started_at, 'MMM d, h:mm a')}{ses.seconds ? ` · ${Math.round(ses.seconds / 60)} min` : ses.ended_at ? '' : ' · live now'}</span>
                    <button className="text-xs font-semibold text-[#A62B1F]" onClick={() => downloadAudio(ses.id)}>Download their audio</button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <Card title="Transcript" actions={[<Button key="r" size="sm" variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>]}>
            {(iv.transcript || []).length === 0 ? <p className="text-sm text-[#5C6670]">Nothing yet. The transcript fills in live as {s.first_name} talks.</p> : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {iv.transcript.map((t, i) => (
                  <div key={i} className="text-sm">
                    <span className={`font-semibold ${t.role === 'user' ? 'text-[#1E2A33]' : 'text-[#A62B1F]'}`}>{t.role === 'user' ? s.first_name : 'Host'}</span>
                    <span className="text-[11px] text-[#5C6670] ml-2">{t.mode}{t.t != null ? ` · ${Math.floor(t.t / 60)}:${String(Math.floor(t.t % 60)).padStart(2, '0')}` : ''}</span>
                    <p className="text-[#1E2A33]">{t.text}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ── Follow-ups ───────────────────────────────────────── */}
        <TabsContent value="followups">
          <Card title="Follow-up questions" actions={[
            <Button key="g" size="sm" variant="outline" disabled={!done.interview || busy === 'gen'} onClick={async () => {
              const r = await run('gen', () => interviewsAPI.generateFollowups(id), 'Checked for missing details');
              if (r) setFu(r.data.followups);
            }}>{busy === 'gen' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Find what's missing</Button>,
            <Button key="s" size="sm" variant="outline" onClick={async () => { const r = await run('savefu', () => interviewsAPI.saveFollowups(id, fu), 'Saved'); if (r) setFu(r.data.followups); }}>Save</Button>,
            <Button key="t" size="sm" className={red} disabled={busy === 'sendfu' || !(fu || []).some((f) => !f.answer && !f.file_id)} onClick={async () => {
              await interviewsAPI.saveFollowups(id, fu);
              await run('sendfu', () => interviewsAPI.sendFollowups(id), `Sent to ${s.first_name}`);
              setFu(null);
            }}><Send className="w-4 h-4 mr-1" />Send to {s.first_name}</Button>,
          ]}>
            <p className="text-xs text-[#5C6670] mb-3">Checks the brief and transcript against the press kit checklist. Edit or add questions, then send them by text and email.</p>
            <div className="space-y-2">
              {(fu || []).map((f, i) => (
                <div key={f.key || i} className="rounded-lg border border-[#E3DDD1] p-3 bg-[#FBF8F2]">
                  <div className="flex gap-2 items-start">
                    <Input value={f.question} onChange={(e) => setFu(fu.map((x, j) => (j === i ? { ...x, question: e.target.value } : x)))} className="bg-white" />
                    <select value={f.kind} onChange={(e) => setFu(fu.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))} className="border border-[#E3DDD1] rounded-md px-2 py-2 text-sm bg-white">
                      <option value="text">Text</option><option value="link">Link</option><option value="photo">Photo / file</option>
                    </select>
                    <button onClick={() => setFu(fu.filter((_, j) => j !== i))} className="p-2 text-[#5C6670] hover:text-[#A62B1F]"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="text-xs mt-1.5 text-[#5C6670]">
                    {f.answer ? <span className="text-emerald-700">Answer: {f.answer}</span>
                      : f.file_id ? <button className="text-emerald-700 underline" onClick={() => openFile(f.file_id, `${f.key}`)}>File received, download</button>
                        : f.asked_at ? `Sent ${safeFormat(f.asked_at, 'MMM d')}, waiting` : 'Not sent yet'}
                  </div>
                </div>
              ))}
              <button type="button" className="text-xs font-semibold text-[#A62B1F]" onClick={() => setFu([...(fu || []), { key: '', question: '', kind: 'text' }])}>+ Add question</button>
            </div>
          </Card>
        </TabsContent>

        {/* ── Package ──────────────────────────────────────────── */}
        <TabsContent value="package">
          <Card title="Press kit" actions={[
            iv.desk_ready && <Button key="desk" size="sm" variant="outline" disabled={atDesk || !done.interview || busy === 'desk'} onClick={sendToDesk}><Bot className="w-4 h-4 mr-1" />{iv.package ? 'Back to the desk' : 'Send to the desk'}</Button>,
            <Button key="d" size="sm" variant="outline" disabled={drafting || !done.interview} onClick={() => {
              if (iv.package && !window.confirm('Redraft the package from scratch? Your edits will be replaced.')) return;
              setPkgDirty(false);
              run('compile', () => interviewsAPI.compile(id), 'Drafting the package…');
            }}>{drafting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}{iv.package ? 'Quick redraft' : 'Quick draft'}</Button>,
            iv.package && <Button key="p" size="sm" variant="outline" onClick={downloadPdf}><Download className="w-4 h-4 mr-1" />PDF</Button>,
            iv.package && <Button key="j" size="sm" variant="outline" onClick={exportJson}><Download className="w-4 h-4 mr-1" />JSON</Button>,
            pkgDirty && <Button key="s" size="sm" className={red} onClick={async () => { await run('savepkg', () => interviewsAPI.savePackage(id, pkg), iv.approval ? 'Saved. Approval was reset, so it needs a fresh sign-off.' : 'Saved'); setPkgDirty(false); }}>Save changes</Button>,
          ]}>
            {iv.package_status === 'error' && <p className="text-sm text-[#A62B1F] mb-3">Drafting failed: {iv.package_error}</p>}
            {drafting && <p className="text-sm text-[#5C6670] mb-3">Writing the bios, quotes and angles from the interview…</p>}
            {atDesk && <div className="mb-3">{deskLine}</div>}
            {iv.desk_notes && iv.package_source === 'desk' && (
              <div className="rounded-lg border border-[#E3DDD1] bg-[#FBF8F2] p-3 mb-4 text-sm">
                <div className="font-semibold mb-1 flex items-center gap-1"><Bot className="w-4 h-4 text-[#A62B1F]" />From the desk{iv.desk_notes.by ? ` (${iv.desk_notes.by})` : ''} · {safeFormat(iv.desk_notes.at, 'MMM d, h:mm a')}</div>
                {iv.desk_notes.summary && <p className="mb-2">{iv.desk_notes.summary}</p>}
                {(iv.desk_notes.doubts || []).length > 0 && <div className="mb-2"><div className="font-semibold text-amber-800">Double-check</div><ul>{iv.desk_notes.doubts.map((d, i) => <li key={i}>• {d}</li>)}</ul></div>}
                {(iv.desk_notes.missing || []).length > 0 && <div className="mb-2"><div className="font-semibold">Still missing</div><ul>{iv.desk_notes.missing.map((d, i) => <li key={i}>• {d}</li>)}</ul>
                  <button className="text-xs font-semibold text-[#A62B1F] mt-1" onClick={() => { setFu([...(fu || []), ...iv.desk_notes.missing.map((m) => ({ key: '', question: m, kind: 'text' }))]); setTab('followups'); }}>Turn into follow-up questions</button></div>}
                {(iv.desk_notes.sources || []).length > 0 && <details><summary className="cursor-pointer text-[#5C6670]">Sources used ({iv.desk_notes.sources.length})</summary>
                  <ul className="mt-1">{iv.desk_notes.sources.map((x, i) => <li key={i} className="truncate">• {x.url ? <a href={x.url} target="_blank" rel="noreferrer" className="underline">{x.title || x.url}</a> : x.title}{x.used_for ? <span className="text-[#5C6670]"> · {x.used_for}</span> : null}</li>)}</ul></details>}
              </div>
            )}
            {!pkg ? <p className="text-sm text-[#5C6670]">{done.interview ? 'Draft the package once the interview and follow-ups are in.' : 'The package is drafted after the interview.'}</p> : (
              <>
                {(pkg.flags || []).length > 0 && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 mb-4 text-sm text-amber-900">
                    <div className="font-semibold mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" />Check before approving</div>
                    <ul>{pkg.flags.map((f, i) => <li key={i}>• {f}</li>)}</ul>
                  </div>
                )}
                <FieldEditor value={Object.fromEntries(Object.entries(pkg).filter(([k]) => k !== 'flags'))}
                  onChange={(v) => { setPkg({ ...v, flags: pkg.flags }); setPkgDirty(true); }} />
              </>
            )}
          </Card>

          {iv.package && (
            <Card title="Final approval">
              {iv.approval ? (
                <div className="text-sm flex items-center gap-3 flex-wrap">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span>Approved by {iv.approval.by_name} on {safeFormat(iv.approval.at, 'MMM d, yyyy')}. {iv.consent ? `${s.first_name} accepted the terms.` : `${s.first_name} has been sent the link.`}</span>
                  {iv.is_requester && !iv.consent && <Button size="sm" variant="outline" onClick={() => run('unapprove', () => interviewsAPI.unapprove(id), 'Approval withdrawn')}>Withdraw approval</Button>}
                </div>
              ) : iv.is_requester ? (
                <div className="text-sm space-y-3">
                  <p>Once you approve, {s.first_name} gets a text and email to accept the terms and open the package. Any later edit resets the approval.</p>
                  <Button className={red} disabled={pkgDirty || busy === 'approve'} onClick={() => run('approve', () => interviewsAPI.approve(id), `Approved and sent to ${s.first_name}`)}>
                    <CheckCircle2 className="w-4 h-4 mr-1" />Approve and send
                  </Button>
                  {pkgDirty && <p className="text-xs text-[#5C6670]">Save your changes first.</p>}
                </div>
              ) : (
                <p className="text-sm text-[#5C6670]">Waiting for {iv.requested_by?.name}, who requested this interview, to give final approval.</p>
              )}
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
